<template>
    <div v-if="!hideWhenInsuranceNotAllowed">
        <b-row>
            <b-col cols="12" md="3">
                <b-button ref="eligibility-check-button" @click="eligibilityCheck" :disabled="disabled"
                    block>Check Eligibility
                </b-button>
            </b-col>
            <b-col cols="12" md="5">
                <label for="cardholder-input">Cardholder Id</label>
                <b-form-input id="cardholder-input" v-model="cardholderId"></b-form-input>
            </b-col>
            <b-col v-if="showRTPB && prescription" cols="12" md="4" lg="3">
                <b-button block @click="getBenefits(undefined)">
                    Check Benefits (RTPB)
                </b-button>
            </b-col>
        </b-row>
        <b-row v-if="results.request">
            <b-col cols="4">
                <h5 class="mb-0">Submitted</h5>
                {{ results.request.formattedName }} - {{ results.request.dob }} -
                {{ results.request.gender }}<br />
                {{ results.request.formattedAddress }}
                <div v-for="response in results.responses" :key="response.controlNumber">
                    {{ response.errorMessage }}
                </div>
            </b-col>
            <b-col cols="8">
                <h5 class="mb-0">Results</h5>
                <card v-for="(result, index) in results.responses" :key="result.controlNumber"
                    @click.native="planClicked(result)">
                    <b-row v-if="!result.errorMessage">
                        <b-col>
                            <strong>{{ formatPayerTier(result.payerEntityCode, index) }}: {{ result.payerName }}</strong>
                            <b-badge v-if="result.providerType === 1" variant="primary" class="ml-1">Medicare</b-badge>
                            <b-badge v-if="result.providerType === 2" variant="info" class="ml-1">Medicaid</b-badge>
                            <br />
                            <strong class="text-muted">{{ result.healthPlanName }}</strong><br />
                            <span
                                :class="{ 'text-danger': result.demographicsChanged }">{{ result.formattedName }} - {{ result.dob }}
                - {{ result.gender }}<br />
                {{ result.formattedAddress }}</span>
                            <b-button v-if="showRTPB && prescription" size="sm"
                                @click.stop.prevent="getBenefits(result.surescriptsPayerId)">
                                Check Benefits (RTPB)
                            </b-button>
                        </b-col>
                        <b-col>
                            <strong>BIN/PCN: </strong>{{ result.bin }}/{{ result.pcn }}<br />
                            <strong>Cardholder Id: </strong>{{ result.cardholderId }}<br />
                            <strong>Group Number: </strong>{{ result.groupNumber }}<br />
                            <strong>Person Code: </strong>{{ result.personCode }}<br />
                            <template v-if="formatInsuranceType(result.insuranceType)">
                                <strong>Insurance Type: </strong>{{ formatInsuranceType(result.insuranceType) }}<br />
                            </template>
                        </b-col>
                        <b-col>
                            <div v-for="coverage in result.coverageTypes" :key="coverage.type">
                                <strong>{{ coverage.type }}: </strong>{{ coverage.status }}
                            </div>
                        </b-col>
                    </b-row>
                    <b-row v-if="result.errorMessage" :click="planClicked">
                        <b-col>
                            <strong>{{ result.healthPlanName }}</strong><br />
                            <span class="text-danger">
                {{ result.errorMessage }}<br />
                {{ result.actionCode }}</span>
                        </b-col>
                    </b-row>
                </card>
            </b-col>
        </b-row>

        <b-modal v-model="showBenefitModal" title="Real Time Prescription Benefit" size="xl" no-fade hide-footer>
            <div v-if="loadingBenefits" class="text-center">
                <b-spinner />
                <div>
                    <strong>Getting Benefits...</strong>
                </div>
            </div>
            <b-alert :show="!!benefitsError" variant="danger">
                <strong>
                    {{ benefitsError }}
                </strong>
            </b-alert>
            <RTPBComponent v-if="benefits" :benefits="benefits" :request-message-id="benefitsRequestMessageId"
                :response-message-id="benefitsResponseMessageId" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, VModel, Emit, Watch, Ref } from "vue-property-decorator";
import { Patient } from "@/models/Patient";
import Axios, { AxiosError } from "axios";
import { PatientPlan } from "@/models/PatientPlan";
import { Plan } from "@/models/Plan";
import { CombinedPlan } from "@/models/CombinedPlan";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { EligibilityResponse, EligibilityResults } from "@/models/EligibilityResults";
import { Prescription } from "@/models/Prescription";
import RTPBComponent from "./RTPBComponent.vue";

@Component({
    name: "EligibilityComponent",
    components: {
        RTPBComponent,
    },
})
export default class EligibilityComponent extends Vue {
    @VModel({ default: null }) private patient!: Patient;
    @Prop({
        type: Boolean,
        default: false,
    }) private disabled!: boolean;
    @Prop({
        type: Number,
        default: 0,
    }) private storeId!: number;
    @Prop({ default: null }) private prescription!: Prescription | null;
    @Prop({
        type: Boolean,
        default: false,
    }) private hideWhenInsuranceNotAllowed!: boolean;
    @Ref('eligibility-check-button') private eligibilityCheckButton!: HTMLElement;
    private cardholderId: string = "";
    // private combinedPlans: CombinedPlan[] = [];
    private results: EligibilityResults = new EligibilityResults();
    // private combinedFields: any[] = [
    //   { key: "plan.bin", label: "BIN" },
    //   { key: "plan.pcn", label: "PCN" },
    //   { key: "patientPlan.memberId", label: "Member Id" },
    //   { key: "patientPlan.groupId", label: "Group Id" },
    //   { key: "patientPlan.cardholderId", label: "Cardholder Id" },
    //   { key: "patientPlan.relationshipCode", label: "Relationship Code" },
    // ];

    mounted() {
        this.getBenefitsConfiguration();
    }

    @Emit("eligibilityCheck")
    eligibilityCheck() {
        return this.sendRequest;
    }

    @Watch("patient")
    patientChanged(patNew: Patient, patOld: Patient) {
        if (patNew.id != patOld.id) {
            this.clear();
        }
    }

    sendRequest() {
        if (!this.patient?.id) {
            this.$notification(NotificationOptions.error("Please select a patient before checking eligibility"));
            return;
        }

        const url = `Insurance/Eligibility/Surescripts/${this.patient.id}`;
        const cardholderId = this.cardholderId;
        Axios.get(url, {
            params: {
                cardholderId: cardholderId,
                storeId: this.storeId,
            },
        })
             .then((response) => {
                 this.results = new EligibilityResults(response.data);
             })
             .catch(err => {
                 this.$notification(NotificationOptions.error(err));
             });
    }

    planClicked(result: EligibilityResponse) {
        console.log(JSON.stringify(result));
        const selectedPlan = new CombinedPlan(null);
        selectedPlan.plan = new Plan();
        selectedPlan.plan.bin = result.bin;
        selectedPlan.plan.pcn = result.pcn;
        selectedPlan.plan.surescriptsPayerId = result.surescriptsPayerId;
        selectedPlan.plan.providerType = result.providerType;
        selectedPlan.patientPlan = new PatientPlan();
        selectedPlan.patientPlan.cardholderId = result.cardholderId;
        selectedPlan.patientPlan.groupId = result.groupNumber;
        selectedPlan.patientPlan.personCode = result.personCode;
        this.$emit('plan-clicked', selectedPlan);
    }

    clear() {
        // this.combinedPlans = [];
        this.cardholderId = "";
        this.results = new EligibilityResults();
    }

    private showBenefitModal = false;
    private loadingBenefits = false;
    private benefits: any = null;
    private benefitsError: any | null = null;
    private showRTPB = false;
    private benefitsRequestMessageId = "";
    private benefitsResponseMessageId = "";

    async getBenefits(payerId?: string) {
        try {
            this.benefits = null;
            this.benefitsError = null;
            this.showBenefitModal = true;
            this.loadingBenefits = true;
            const resp = await this.$http.post(`/Insurance/RealTimeBenefits`, this.prescription, {
                params: {
                    payerId,
                },
            });
            this.benefits = resp.data.benefits;
            this.benefitsRequestMessageId = resp.data.requestMessageId;
            this.benefitsResponseMessageId = resp.data.responseMessageId;
        } catch (err) {
            const error = err as AxiosError;
            console.warn(error);
            this.benefitsError = error.response?.data ?? "Unknown error";
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loadingBenefits = false;
        }
    }

    async getBenefitsConfiguration() {
        try {
            const resp = await this.$http.get("/Configuration/enableRTPB");
            this.showRTPB = resp.data;
        } catch {
            //ignore
        }
    }

    formatInsuranceType(code: string): string {
        const map: Record<string, string> = {
            "C1": "Commercial",
            "MA": "Medicare Part A",
            "MB": "Medicare Part B",
            "MC": "Medicaid",
            "MP": "Medicare Primary",
            "OT": "Other",
            "WC": "Workers' Compensation",
        };
        return code ? (map[code] ?? code) : "";
    }

    formatPayerTier(entityCode: string, index: number): string {
        const tierMap: Record<string, string> = {
            "PRP": "Primary Insurance",
            "SEP": "Secondary Insurance",
            "TTP": "Tertiary Insurance",
        };
        if (entityCode && tierMap[entityCode]) return tierMap[entityCode];
        const fallback = ["Primary Insurance", "Secondary Insurance", "Tertiary Insurance"];
        return fallback[index] ?? `Insurance ${index + 1}`;
    }

    public focus() {
        this.eligibilityCheckButton.focus();
    }
}
</script>
